import { Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  Maybe,
  MyRankingQuery,
  RankingPeriodType,
  RankingStatus,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { RankListItem, RankListItemSkeleton } from '@/components/organisms'
import { ScrollFetcher, Segment } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'
export * from './fragments'

export type State = {
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  period?: string
  periodType: RankingPeriodType
  rankings: Maybe<MyRankingQuery['organizationArtistRankings']>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: (periodType: RankingPeriodType) => Promise<void>
  onChangePeriod: (periodType: RankingPeriodType) => Promise<void>
}

export const MyRankingTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onChangePeriod,
}) => {
  const { t } = useTranslation('myRanking')
  const { routerNames } = useRouter()
  const [currentSegment, setCurrentSegment] = useState<number>(0)
  const { onClick } = useBrowserLink(getHelpUrl('ranking'))

  useEffect(() => {
    setCurrentSegment(state.periodType === RankingPeriodType.Monthly ? 0 : 1)
  }, [state.periodType])

  const handleChangeSegment = useCallback(
    async (segment: number) => {
      setCurrentSegment(segment)
      await onChangePeriod(
        segment === 0 ? RankingPeriodType.Monthly : RankingPeriodType.Yearly
      )
    },
    [onChangePeriod]
  )

  return (
    <>
      <Head title={routerNames.MyRanking} />
      <BaseLayout
        margin
        header={{
          back: true,
          title: t('title'),
          right: (
            <IconButton.Help
              edge="end"
              color="primary.main"
              onClick={onClick}
            />
          ),
        }}
      >
        <Segment
          labels={[t('month'), t('total')]}
          value={currentSegment}
          disableAnimateTransitions
          onChange={handleChangeSegment}
        >
          <RankingsCell
            loading={state.loading || state.refetching}
            hasNextPage={state.hasNextPage}
            onFetchMore={() =>
              onFetchMore(
                currentSegment === 0
                  ? RankingPeriodType.Monthly
                  : RankingPeriodType.Yearly
              )
            }
            rankings={
              currentSegment === 0 && !state.loading ? state.rankings : null
            }
            note={
              state.period
                ? t('aggregationPeriod', { period: state.period })
                : undefined
            }
          />
          <RankingsCell
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            onFetchMore={() =>
              onFetchMore(
                currentSegment === 0
                  ? RankingPeriodType.Monthly
                  : RankingPeriodType.Yearly
              )
            }
            rankings={
              currentSegment === 1 && !state.loading ? state.rankings : null
            }
          />
        </Segment>
      </BaseLayout>
    </>
  )
}

type RankingsCellProps = {
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  onFetchMore: () => Promise<void>
  rankings: State['rankings']
  note?: string
}
const RankingsCell: Cell<RankingsCellProps> = ({
  loading,
  rankings,
  note,
  hasNextPage,
  onFetchMore,
}) => {
  const { t } = useTranslation('myRanking')
  return (
    <CellFrame>
      <Stack spacing={2}>
        {!!note && (
          <Typography variant="caption" color="text.hint">
            {note}
          </Typography>
        )}
        <Typography variant="h6" color="text.secondary">
          {t('currentStanding')}
        </Typography>
        <Stack>
          <ScrollFetcher
            nodes={rankings?.edges
              .filter(({ node }) => node?.status === RankingStatus.Active)
              .map(
                ({ node }) =>
                  node && (
                    <RankListItem
                      key={node.id}
                      displayName={node.artist?.displayName ?? ''}
                      avatarUrl={node.artist?.avatar?.smallUri ?? ''}
                      point={node.latestRankingPeriod?.myRanking?.point ?? 0}
                      rank={node.latestRankingPeriod?.myRanking?.rank ?? 0}
                      userId={node.artistId}
                    />
                  )
              )}
            componentProps={{ spacing: 2 }}
            loading={loading}
            hasNextPage={hasNextPage}
            onFetchMore={onFetchMore}
            loadingComponent={
              <Stack spacing={2}>
                {Array.from(Array(4).keys()).map(index => (
                  <RankListItemSkeleton key={index} />
                ))}
              </Stack>
            }
          />
        </Stack>
      </Stack>
    </CellFrame>
  )
}
